import React, { useEffect, useRef, useState } from "react";
import {
  Card,
  Form,
  Input,
  Button,
  Table,
  Modal,
  Select,
  Tag,
  Popconfirm,
  Space,
} from "antd";
import { PageHeader } from "@ant-design/pro-layout";
import {
  HomeOutlined,
  DeleteOutlined,
  PercentageOutlined,
  EditOutlined,
} from "@ant-design/icons";
import {
  getBranches,
  getAllDiscounts,
  createDiscount,
  updateDiscount,
  deleteDiscount,
} from "../../../network/network";
import CustomBreadcrumb from "../../../components/CustomBreadcrumb";
import { handleApiError } from "../../../utils/errorHandler";
import NotificationWithIcon from "../../../components/NotificationWithIcon";
import { useTranslation } from "react-i18next";

const Discounts = () => {
  const ADD_DISCOUNT = "Add Discount";
  const [loadingBTN, setLoadingBTN] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [isDataLoading, setIsDataLoading] = useState(false);
  const [reloadTableData, setReloadTableData] = useState(false);
  const [discountTableData, setDiscountTableData] = useState([]);
  const [branchesData, setBranchesData] = useState([]);
  const [modalTitle, setModalTitle] = useState(ADD_DISCOUNT);
  const [addDiscountForm] = Form.useForm();
  const { t } = useTranslation();
  const branchSelect = useRef();

  useEffect(() => {
    setIsDataLoading(true);
    getAllDiscounts()
      .then((response) => {
        if (response?.data?.success === false) {
          handleApiError(response, t);
          return;
        }

        setDiscountTableData(response.data.data.discounts);
      })
      .catch((error) => handleApiError(error, t))
      .finally(() => {
        setIsDataLoading(false);
      });
  }, [reloadTableData, t]);

  useEffect(() => {
    setIsDataLoading(true);
    getBranches({ per_page: -1 })
      .then((response) => {
        if (response?.data?.success === false) {
          handleApiError(response, t);
          return;
        }

        setBranchesData(response.data.data.branches);
      })
      .catch((error) => handleApiError(error, t))
      .finally(() => {
        setIsDataLoading(false);
      });
  }, [t]);

  const showModal = () => {
    // Show Modal
    setModalVisible(true);
    // Set Modal Title
    setModalTitle(t("discount_add"));
    // Reset Form Fields
    addDiscountForm.resetFields();
  };

  const handleAPIResponse = (response, type) => {
    if (response.data.success === true) {
      setReloadTableData(!reloadTableData);
      // Success
      if (type === ADD_DISCOUNT) {
        NotificationWithIcon(
          "success",
          t("discount_added"),
          t("discount_added_successfully"),
        );
      } else {
        NotificationWithIcon(
          "success",
          t("discount_updated"),
          t("discount_updated_successfully"),
        );
      }
    } else {
      handleApiError(response, t);
    }
    // Reset Form Fields
    addDiscountForm.resetFields();
    // Hide Modal
    setModalVisible(false);
  };

  const handleAddEdit = () => {
    addDiscountForm
      .validateFields()
      .then((values) => {
        // Set Loading True
        setLoadingBTN(true);
        // Sending Request to API
        if (modalTitle === ADD_DISCOUNT || modalTitle === t("discount_add")) {
          createDiscount(values)
            .then((response) => handleAPIResponse(response, ADD_DISCOUNT))
            .catch((error) => handleApiError(error, t))
            .finally(() => {
              // Set Loading False
              setLoadingBTN(false);
            });
        } else {
          updateDiscount(values)
            .then((response) => handleAPIResponse(response, null))
            .catch((error) => handleApiError(error, t))
            .finally(() => {
              // Set Loading False
              setLoadingBTN(false);
            });
        }
      })
      .catch((info) => {
        console.log("Validate Failed:", info);
      });
  };

  const handleEditModal = (id) => {
    // Find discount from DataArray
    const discount = discountTableData.find((discount) => discount.id === id);

    // Set Modal Title
    setModalTitle(t("discount_edit") + " : " + discount.name);

    // Show Modal
    setModalVisible(true);

    // Explicitly reset Form Fields before setting them for edit
    // This ensures the form is in a clean state before being repopulated
    addDiscountForm.resetFields();

    // Set Form Fields
    addDiscountForm.setFieldsValue({
      id: discount.id,
      discount_percentage: discount.discount_percentage,
      branches: discount.branches.map(({ id }) => id),
    });
  };

  const handleCancel = () => {
    // Reset Form Fields
    addDiscountForm.resetFields();
    setModalVisible(false);
  };

  const handleDelete = (id) => {
    deleteDiscount(id)
      .then((response) => {
        if (response.data.success === true) {
          setReloadTableData(!reloadTableData);
          // Success
          NotificationWithIcon(
            "success",
            t("discount_deleted"),
            t("discount_deleted_successfully"),
          );
        } else {
          handleApiError(response, t);
        }
      })
      .catch((error) => handleApiError(error, t));
  };

  const breadcrumbRoutes = [
    {
      path: "/",
      breadcrumbName: t("home"),
      icon: <HomeOutlined />,
    },
    {
      path: "discounts",
      breadcrumbName: t("discounts"),
      icon: <PercentageOutlined />,
    },
  ];

  const DiscountTableColumns = [
    {
      title: "#",
      key: "index",
      className: "app-table-index-column",
      render: (text, record, index) => {
        return <p>{index + 1}</p>;
      },
    },
    {
      title: t("discount_percentage"),
      dataIndex: "discount_percentage",
      render: (discount_percentage) => {
        return <>{discount_percentage} %</>;
      },
    },
    {
      title: t("branches"),
      dataIndex: "branches",
      render: (branches) => {
        return branches.map((branch, i) => {
          return (
            <Tag color="blue" key={i}>
              {branch.name}
            </Tag>
          );
        });
      },
    },

    {
      title: t("action"),
      align: "center",
      width: 100,
      render: (record) => {
        return (
          <Space>
            <Button
              type="link"
              onClick={() => handleEditModal(record.id)}
              title={t("discount_edit")}
            >
              <EditOutlined />
            </Button>
            <Popconfirm
              title={t("warning_delete")}
              onConfirm={() => handleDelete(record.id)}
              okText={t("yes")}
              cancelText={t("no")}
              placement="topLeft"
            >
              <Button type="link" title={t("delete_entity")}>
                <DeleteOutlined />
              </Button>
            </Popconfirm>
          </Space>
        );
      },
    },
  ];

  return (
    <>
      <Card className="app-mb-20">
        <CustomBreadcrumb menus={breadcrumbRoutes} />
        <PageHeader
          className="app-p-0"
          title={t("discounts")}
          ghost={false}
          extra={[
            <Button
              key="open-add-discount-modal"
              type="primary"
              onClick={showModal}
            >
              + {t("discount_add")}
            </Button>,
          ]}
        />
      </Card>
      <Card>
        <Table
          columns={DiscountTableColumns}
          dataSource={discountTableData}
          loading={isDataLoading}
          rowKey="id"
          pagination={false}
        />
      </Card>
      <Modal
        open={modalVisible}
        title={modalTitle}
        onOk={handleAddEdit}
        onCancel={handleCancel}
        maskClosable={false}
        footer={[
          <Button
            type="default"
            key="back"
            loading={loadingBTN}
            onClick={handleCancel}
          >
            {t("cancel")}
          </Button>,
          <Button
            key="submit"
            type="primary"
            loading={loadingBTN}
            onClick={handleAddEdit}
          >
            {modalTitle === ADD_DISCOUNT || modalTitle === t("discount_add")
              ? t("discount_add")
              : t("update")}
          </Button>,
        ]}
      >
        <Form
          name="add-discount-form"
          layout="vertical"
          onFinish={handleAddEdit}
          className="app-form-full"
          form={addDiscountForm}
        >
          <Form.Item
            name="discount_percentage"
            label={t("discount_percentage")}
            rules={[
              {
                required: true,
                message: t("validate_discount_required"),
              },
            ]}
            hasFeedback
          >
            <Input type="number" />
          </Form.Item>

          <Form.Item
            name="branches"
            label={t("branches")}
            rules={[
              {
                required: true,
                message: t("validate_branch_required"),
              },
            ]}
            hasFeedback
          >
            <Select
              showSearch
              mode="multiple"
              allowClear
              onChange={() => branchSelect.current.blur()}
              ref={branchSelect}
              options={branchesData.map((branch) => {
                return {
                  label: branch.name,
                  value: branch.id,
                };
              })}
              filterOption={(input, option) =>
                (option?.label ?? "")
                  .toLowerCase()
                  .includes(input.toLowerCase())
              }
            />
          </Form.Item>

          <Form.Item name="id" className="app-hidden">
            <input type="hidden" />
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
};

export default Discounts;
